import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DragDropModule, CdkDragDrop, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { NgbModule, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { SearchComponent } from '../../../../_shared/components/filters/search/search.component';
import { DateFilterComponent } from '../../../../_shared/components/filters/date-filter/date-filter.component';
import { DateIntervalFilterData } from '../../../../_shared/models';
import { DateInterval } from '../../../../_shared/enums';
import {
  CalendarDay,
  CalendarEvent,
  CalendarTodayEvent,
  CalendarFilterOption,
  CalendarFilterSection,
  CalendarConfig
} from '../../models/calendar.model';
import { isBefore, startOfDay } from 'date-fns';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-shared-calendar',
  standalone: true,
  imports: [
    TranslocoDirective,
    CommonModule,
    FormsModule,
    RouterModule,
    DateFilterComponent,
    SearchComponent,
    NgbModule,
    DragDropModule,
    InfiniteScrollModule
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss'
})
export class SharedCalendarComponent implements OnInit, OnDestroy, OnChanges {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  @ViewChild('weekTimeline')
  set weekTimelineRef(element: ElementRef<HTMLDivElement> | undefined) {
    this.disconnectWeekTimelineObserver();
    this.weekTimeline = element;
    this.observeWeekTimeline();
    this.updateWeekTimelineScrollbarWidth();
  }

  // Configuration
  @Input() config: CalendarConfig = {
    title: this.transloco.translate('assetShared.calendar.title'),
    filterSectionTitle: this.transloco.translate('assetShared.calendar.filter'),
    filterOptions: [],
    showTodaySection: true,
    todaySectionTitle: this.transloco.translate('assetShared.calendar.today'),
    searchPlaceholder: this.transloco.translate('assetShared.calendar.searchPlaceholder')
  };

  // Data inputs
  @Input() events: CalendarEvent[] = [];
  @Input() expandedDayEvents: CalendarEvent[] = [];
  @Input() expandedDayKey: string | null = null;
  @Input() todayEvents: CalendarTodayEvent[] = [];
  @Input() isLoadingEvents: boolean = false;
  @Input() isLoadingTodayEvents: boolean = false;
  @Input() expandedDayLoadingKey: string | null = null;
  @Input() todayPage: number = 1;

  // Date filter inputs
  @Input() defaultInterval: DateInterval = DateInterval.Month;
  @Input() disabledIntervals: DateInterval[] = [DateInterval.Half];

  // Drag-and-drop guards — opt-in so consumers that want to accept past-date drops
  // (e.g. the workshop calendar) keep their current behaviour.
  @Input() disablePastDrops: boolean = false;

  // Outputs
  @Output() eventClick = new EventEmitter<CalendarEvent>();
  @Output() eventDrop = new EventEmitter<{ event: CalendarEvent; targetDay: CalendarDay }>();
  @Output() filterChange = new EventEmitter<CalendarFilterOption[]>();
  @Output() filterSectionsChange = new EventEmitter<CalendarFilterSection[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() dateFilterChange = new EventEmitter<DateIntervalFilterData>();
  @Output() dateChange = new EventEmitter<{ currentDate: Date; selectedDate: Date }>();
  @Output() monthDayExpand = new EventEmitter<CalendarDay>();
  @Output() todayScrolled = new EventEmitter<void>();

  // Calendar state
  currentDate: Date = new Date();
  selectedDate: Date = new Date();
  miniCalendarDate: Date = new Date();
  calendarDays: CalendarDay[] = [];
  miniCalendarDays: CalendarDay[] = [];
  weekViewDays: CalendarDay[] = [];
  weekTimelineScrollbarWidth: number = 0;

  // View state
  selectedView: 'month' | 'week' | 'day' = 'month';
  searchTerm: string = '';
  todayExpanded: boolean = false;
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  isDraggingOverDisabledDropList: boolean = false;

  // Day/Week view
  dayViewHours: number[] = Array.from({ length: 24 }, (_, i) => i);

  // Internal filter state
  filterState: Map<string, boolean> = new Map();

  get weekDays(): string[] {
    return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(day =>
      this.transloco.translate('assetShared.calendar.weekDays.' + day));
  }
  private isDraggingCalendarEvent: boolean = false;
  private draggedEventSourceDropListId: string | null = null;
  private weekTimeline?: ElementRef<HTMLDivElement>;
  private weekTimelineResizeObserver?: ResizeObserver;

  // Dates follow the app language (not the browser locale) so they match the rest of the translated UI.
  private get dateLocale(): string {
    return this.transloco.getActiveLang();
  }

  get currentMonthName(): string {
    return this.capitalizeFirst(this.currentDate.toLocaleString(this.dateLocale, { month: 'long' }));
  }

  get currentYear(): number {
    return this.currentDate.getFullYear();
  }

  get miniCalendarMonthYear(): string {
    const monthName = this.capitalizeFirst(this.miniCalendarDate.toLocaleString(this.dateLocale, { month: 'long' }));
    const year = this.miniCalendarDate.getFullYear();
    return `${monthName} ${year}`;
  }

  get availableYears(): number[] {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) years.push(i);
    return years;
  }

  get visibleCalendarDays(): CalendarDay[] {
    return this.calendarDays;
  }

  get calendarWeekCount(): number {
    return Math.max(1, Math.ceil(this.calendarDays.length / 7));
  }

  get visibleTodayEvents(): CalendarTodayEvent[] {
    if (this.todayExpanded) return this.todayEvents;
    return this.todayEvents.slice(0, 1);
  }

  ngOnInit(): void {
    this.initializeFilterState();
    this.syncMiniCalendarDateToCurrentDate();
    this.generateCalendar();
    this.generateMiniCalendar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['events'] && !changes['events'].firstChange) {
      this.populateEventsToCalendar();
    }
    if (changes['isLoadingTodayEvents']?.currentValue === true || (changes['todayEvents'] && !changes['todayEvents'].firstChange)) {
      this.todayExpanded = false;
    }
    if (changes['config'] && !changes['config'].firstChange) {
      this.initializeFilterState();
    }
  }

  ngOnDestroy(): void {
    this.disconnectWeekTimelineObserver();
  }

  private initializeFilterState(): void {
    this.filterState.clear();
    this.config.filterOptions.forEach(opt => {
      this.filterState.set(opt.key, opt.checked);
    });
    this.config.filterSections?.forEach(section => {
      section.options.forEach(opt => {
        this.filterState.set(opt.key, opt.checked);
      });
    });
    if (this.config.missedEventFilter) {
      this.filterState.set(this.config.missedEventFilter.key, this.config.missedEventFilter.checked);
    }
  }

  // Filter methods
  isFilterChecked(key: string): boolean {
    return this.filterState.get(key) ?? false;
  }

  onFilterToggle(option: CalendarFilterOption): void {
    const newValue = !this.filterState.get(option.key);
    this.filterState.set(option.key, newValue);
    option.checked = newValue;
    this.todayExpanded = false;
    this.filterChange.emit(this.config.filterOptions);
  }

  onFilterSectionToggle(section: CalendarFilterSection, option: CalendarFilterOption): void {
    const newValue = !this.filterState.get(option.key);
    this.filterState.set(option.key, newValue);
    option.checked = newValue;
    this.todayExpanded = false;
    this.filterSectionsChange.emit(this.config.filterSections ?? []);
  }

  onMissedFilterToggle(): void {
    const option = this.config.missedEventFilter;
    if (!option || this.isMissedFilterDisabled()) return;

    const newValue = !this.filterState.get(option.key);
    this.filterState.set(option.key, newValue);
    option.checked = newValue;
    this.todayExpanded = false;
    this.filterChange.emit(this.config.filterOptions);
  }

  private findFilterOption(key: string): CalendarFilterOption | undefined {
    return this.config.filterOptions.find(o => o.key === key)
      ?? this.config.filterSections
        ?.flatMap(section => section.options)
        .find(o => o.key === key)
      ?? (this.config.missedEventFilter?.key === key ? this.config.missedEventFilter : undefined);
  }

  getFilterColor(key: string): string {
    return this.findFilterOption(key)?.color || '#9FA1A4';
  }

  getFilterLabel(key: string): string {
    return this.findFilterOption(key)?.label || this.transloco.translate('common.unknown');
  }

  // `event.type` is a stable filter key (used for colours/filtering); show the consumer's translated label for it.
  getEventTypeLabel(type: string): string {
    return this.findFilterOption(type)?.label || type;
  }

  getFilterTextColor(key: string): string {
    return this.findFilterOption(key)?.textColor || '#4D4749';
  }

  getTodayEventColor(event: CalendarTodayEvent): string {
    if (event.isMissedEvent) {
      return this.getFilterColor('missed');
    }

    return this.getFilterColor(event.type);
  }

  hasAnyTypeFiltersChecked(): boolean {
    return this.config.filterOptions.some(option => this.isFilterChecked(option.key));
  }

  isMissedFilterDisabled(): boolean {
    return !this.hasAnyTypeFiltersChecked();
  }

  formatTodayEventTime(value: Date): string {
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private observeWeekTimeline(): void {
    if (!this.weekTimeline?.nativeElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.weekTimelineResizeObserver = new ResizeObserver(() => {
      this.updateWeekTimelineScrollbarWidth();
    });

    this.weekTimelineResizeObserver.observe(this.weekTimeline.nativeElement);
  }

  private disconnectWeekTimelineObserver(): void {
    this.weekTimelineResizeObserver?.disconnect();
    this.weekTimelineResizeObserver = undefined;
  }

  private updateWeekTimelineScrollbarWidth(): void {
    const element = this.weekTimeline?.nativeElement;
    if (!element) {
      this.weekTimelineScrollbarWidth = 0;
      return;
    }

    this.weekTimelineScrollbarWidth = Math.max(0, element.offsetWidth - element.clientWidth);
  }

  // Date filter
  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;

    if (filter.interval === DateInterval.Day) {
      this.selectedView = 'day';
    } else if (filter.interval === DateInterval.Week) {
      this.selectedView = 'week';
      this.generateWeekDays();
    } else {
      this.selectedView = 'month';
    }

    this.dateFilterChange.emit(filter);
  }

  // Navigation
  goToToday(): void {
    this.currentDate = new Date();
    this.selectedDate = new Date();
    this.syncMiniCalendarDateToCurrentDate();
    this.generateCalendar();
    this.generateMiniCalendar();
    if (this.selectedView === 'week') {
      this.generateWeekDays();
    }
    this.emitDateChange();
  }

  previousMonth(): void {
    this.shiftCommittedMonth(-1);
    this.syncMiniCalendarDateToCurrentDate();
    this.generateCalendar();
    if (this.selectedView === 'week') {
      this.generateWeekDays();
    }
    this.generateMiniCalendar();
    this.emitDateChange();
  }

  nextMonth(): void {
    this.shiftCommittedMonth(1);
    this.syncMiniCalendarDateToCurrentDate();
    this.generateCalendar();
    if (this.selectedView === 'week') {
      this.generateWeekDays();
    }
    this.generateMiniCalendar();
    this.emitDateChange();
  }

  previousMiniMonth(): void {
    this.miniCalendarDate = new Date(this.miniCalendarDate.getFullYear(), this.miniCalendarDate.getMonth() - 1, 1);
    this.generateMiniCalendar();
  }

  nextMiniMonth(): void {
    this.miniCalendarDate = new Date(this.miniCalendarDate.getFullYear(), this.miniCalendarDate.getMonth() + 1, 1);
    this.generateMiniCalendar();
  }

  selectYear(year: number): void {
    this.currentDate = new Date(year, this.currentDate.getMonth(), 1);
    this.selectedDate = new Date(year, this.selectedDate.getMonth(), this.selectedDate.getDate());
    this.syncMiniCalendarDateToCurrentDate();
    this.generateCalendar();
    this.generateMiniCalendar();
    if (this.selectedView === 'week') {
      this.generateWeekDays();
    }
    this.emitDateChange();
  }

  selectDay(day: CalendarDay): void {
    if (day.isCurrentMonth) {
      this.selectedDate = day.date;
      if (this.selectedView === 'week') {
        this.generateWeekDays();
      }
    }
  }

  selectMiniDay(day: CalendarDay): void {
    this.selectedDate = day.date;
    this.currentDate = new Date(day.date.getFullYear(), day.date.getMonth(), 1);
    this.syncMiniCalendarDateToCurrentDate();
    this.generateCalendar();
    this.generateMiniCalendar();
    if (this.selectedView === 'week') {
      this.generateWeekDays();
    }
    this.emitDateChange();
  }

  isSelectedDay(day: CalendarDay): boolean {
    return day.date.toDateString() === this.selectedDate.toDateString();
  }

  private emitDateChange(): void {
    this.dateChange.emit({
      currentDate: this.currentDate,
      selectedDate: this.selectedDate
    });
  }

  // Search
  onSearchChange(term: any): void {
    this.searchTerm = term;
    this.searchChange.emit(term);
  }

  // Today section
  toggleTodayExpanded(): void {
    this.todayExpanded = !this.todayExpanded;
  }

  onTodayScrolled(): void {
    if (!this.todayExpanded) return;
    this.todayScrolled.emit();
  }

  // Events
  onEventClick(event: CalendarEvent): void {
    this.eventClick.emit(event);
  }

  onEventDrop(dropEvent: CdkDragDrop<CalendarEvent[]>, targetDay: CalendarDay): void {
    this.clearDropHoverState();

    if (!this.canEmitCalendarEventDrop(dropEvent, this.isDayDropDisabled(targetDay))) {
      return;
    }

    const movedEvent = dropEvent.item.data as CalendarEvent | undefined;
    if (movedEvent) {
      this.eventDrop.emit({ event: movedEvent, targetDay });
    }
  }

  onCalendarEventDragStarted(event: CalendarEvent): void {
    this.isDraggingCalendarEvent = true;
    this.isDraggingOverDisabledDropList = false;
    this.draggedEventSourceDropListId = this.getEventDropListId(event);
  }

  onCalendarEventDragEnded(): void {
    this.clearDropHoverState();
  }

  onDayDropListPointerEnter(day: CalendarDay): void {
    if (!this.isDraggingCalendarEvent) {
      return;
    }

    this.isDraggingOverDisabledDropList = this.isDayDropDisabled(day);
  }

  onDayDropListPointerLeave(day: CalendarDay): void {
    if (this.isDayDropDisabled(day)) {
      this.isDraggingOverDisabledDropList = false;
    }
  }

  shouldDisableDropListSorting(day: CalendarDay): boolean {
    return (
      this.isDraggingOverDisabledDropList &&
      this.draggedEventSourceDropListId === this.getDropListId(day)
    );
  }

  getVisibleEvents(day: CalendarDay): CalendarEvent[] {
    return day.events.slice(0, 3);
  }

  getRemainingEventsCount(day: CalendarDay): number {
    return Math.max(0, day.events.length - 3);
  }

  getDropListId(day: CalendarDay): string {
    return `day-${day.date.toISOString()}`;
  }

  isDayDropDisabled(day: CalendarDay): boolean {
    if (!this.disablePastDrops) return false;
    const today = startOfDay(new Date());
    const dayStart = startOfDay(day.date);
    return isBefore(dayStart, today);
  }

  // Drop-only gate: we still want past-date cells to act as drag SOURCES
  // (so events already scheduled in the past can be moved forward), we just
  // want to reject drops ONTO past-date cells. Using cdkDropListDisabled would
  // disable both directions, so we use cdkDropListEnterPredicate instead.
  canDropOnList = (_drag: CdkDrag, drop: CdkDropList): boolean => {
    const day = this.calendarDays.find(d => this.getDropListId(d) === drop.id);
    if (this.isDraggingOverDisabledDropList) {
      return drop.id === this.draggedEventSourceDropListId;
    }

    return !day || !this.isDayDropDisabled(day);
  };

  onMonthDayExpand(day: CalendarDay, popover: NgbPopover): void {
    this.monthDayExpand.emit(day);
    popover.open();
  }

  isExpandedDayLoading(day: CalendarDay): boolean {
    return this.expandedDayLoadingKey === this.getDayKey(day.date);
  }

  getPopoverEvents(day: CalendarDay): CalendarEvent[] {
    if (this.expandedDayKey && this.getDayKey(day.date) === this.expandedDayKey) {
      return this.expandedDayEvents;
    }

    const matchingDay = this.calendarDays.find(calendarDay =>
      calendarDay.date.toDateString() === day.date.toDateString()
    );

    return matchingDay?.events ?? [];
  }

  getEventColor(event: CalendarEvent): string {
    if (event.isMissedEvent) {
      return this.getFilterColor('missed');
    }

    return this.getFilterColor(event.type);
  }

  getEventBackgroundColor(event: CalendarEvent): string | null {
    return event.colorKey ? this.getFilterColor(event.colorKey) : null;
  }

  getEventTextColor(event: CalendarEvent): string | null {
    return event.colorKey ? this.getFilterTextColor(event.colorKey) : null;
  }

  hasEventBackground(event: CalendarEvent): boolean {
    return Boolean(event.colorKey);
  }

  formatPopoverDate(day: CalendarDay): string {
    const weekday = this.capitalizeFirst(day.date.toLocaleDateString(this.dateLocale, { weekday: 'long' }));
    const dayNum = day.date.getDate();
    const month = this.capitalizeFirst(day.date.toLocaleDateString(this.dateLocale, { month: 'short' }));
    const year = day.date.getFullYear();
    return `${weekday} ${dayNum} ${month} ${year}`;
  }

  // Day view
  get selectedDayName(): string {
    return this.selectedDate.toLocaleDateString(this.dateLocale, { weekday: 'short' }).toUpperCase();
  }

  get selectedDayNumber(): number {
    return this.selectedDate.getDate();
  }

  get isSelectedDayToday(): boolean {
    const today = new Date();
    return this.selectedDate.toDateString() === today.toDateString();
  }

  formatHour(hour: number): string {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  }

  getEventsForHour(hour: number): CalendarEvent[] {
    const dayEvents = this.getSelectedDayEvents();
    return dayEvents.filter(event => parseInt(event.startTime.split(':')[0], 10) === hour);
  }

  getVisibleEventsForHour(hour: number): CalendarEvent[] {
    return this.getEventsForHour(hour).slice(0, 2);
  }

  getRemainingEventsForHour(hour: number): number {
    return Math.max(0, this.getEventsForHour(hour).length - 2);
  }

  getSelectedDayEvents(): CalendarEvent[] {
    const selectedDay = this.calendarDays.find(d => d.date.toDateString() === this.selectedDate.toDateString());
    return selectedDay?.events || [];
  }

  // Week view
  getWeekDayName(day: CalendarDay): string {
    return day.date.toLocaleDateString(this.dateLocale, { weekday: 'short' }).toUpperCase();
  }

  getEventsForDayAndHour(day: CalendarDay, hour: number): CalendarEvent[] {
    return day.events.filter(event => parseInt(event.startTime.split(':')[0], 10) === hour);
  }

  getPopoverEventsForHour(hour: number): CalendarEvent[] {
    return this.getEventsForHour(hour);
  }

  getPopoverEventsForDayAndHour(day: CalendarDay, hour: number): CalendarEvent[] {
    const matchingDay = this.weekViewDays.find(weekDay =>
      weekDay.date.toDateString() === day.date.toDateString()
    );

    return this.getEventsForDayAndHour(matchingDay ?? day, hour);
  }

  getVisibleEventsForDayAndHour(day: CalendarDay, hour: number): CalendarEvent[] {
    return this.getEventsForDayAndHour(day, hour).slice(0, 2);
  }

  getRemainingEventsForDayAndHour(day: CalendarDay, hour: number): number {
    return Math.max(0, this.getEventsForDayAndHour(day, hour).length - 2);
  }

  formatPopoverHour(hour: number): string {
    return this.formatHour(hour);
  }

  isWeekDayToday(day: CalendarDay): boolean {
    const today = new Date();
    return day.date.toDateString() === today.toDateString();
  }

  private canEmitCalendarEventDrop(dropEvent: CdkDragDrop<CalendarEvent[]>, isTargetDayDisabled: boolean): boolean {
    return (
      dropEvent.isPointerOverContainer &&
      dropEvent.previousContainer !== dropEvent.container &&
      !isTargetDayDisabled
    );
  }

  private clearDropHoverState(): void {
    this.isDraggingCalendarEvent = false;
    this.isDraggingOverDisabledDropList = false;
    this.draggedEventSourceDropListId = null;
  }

  private getEventDropListId(event: CalendarEvent): string | null {
    const eventDate = this.getEventDate(event);
    if (!eventDate) {
      return null;
    }

    return `day-${eventDate.toISOString()}`;
  }

  // Calendar generation
  private generateCalendar(): void {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.calendarDays = [];

    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const dayNumber = daysInPrevMonth - i;
      const date = new Date(year, month - 1, dayNumber);
      this.calendarDays.push({
        date,
        dayNumber,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      this.calendarDays.push({
        date,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }

    const totalCells = Math.ceil((startingDayOfWeek + daysInMonth) / 7) * 7;
    const remainingDays = totalCells - this.calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      this.calendarDays.push({
        date,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }

    this.populateEventsToCalendar();
  }

  private generateMiniCalendar(): void {
    const year = this.miniCalendarDate.getFullYear();
    const month = this.miniCalendarDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.miniCalendarDays = [];

    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const dayNumber = daysInPrevMonth - i;
      const date = new Date(year, month - 1, dayNumber);
      this.miniCalendarDays.push({
        date,
        dayNumber,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      this.miniCalendarDays.push({
        date,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }

    const remainingDays = 42 - this.miniCalendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      this.miniCalendarDays.push({
        date,
        dayNumber: day,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }
  }

  private syncMiniCalendarDateToCurrentDate(): void {
    this.miniCalendarDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
  }

  private shiftCommittedMonth(monthDelta: number): void {
    const targetMonthDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + monthDelta, 1);
    const selectedDay = this.selectedDate.getDate();
    const lastDayOfTargetMonth = new Date(targetMonthDate.getFullYear(), targetMonthDate.getMonth() + 1, 0).getDate();
    const clampedDay = Math.min(selectedDay, lastDayOfTargetMonth);

    this.currentDate = targetMonthDate;
    this.selectedDate = new Date(targetMonthDate.getFullYear(), targetMonthDate.getMonth(), clampedDay);
  }

  private generateWeekDays(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(this.selectedDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    this.weekViewDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);

      this.weekViewDays.push({
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === this.currentDate.getMonth(),
        isToday: date.toDateString() === today.toDateString(),
        events: []
      });
    }

    this.populateWeekEvents();
  }

  private populateEventsToCalendar(): void {
    this.calendarDays.forEach(d => (d.events = []));

    this.events.forEach(ev => {
      const eventDate = this.getEventDate(ev);
      if (eventDate) {
        const target = this.calendarDays.find(d =>
          d.date.toDateString() === eventDate.toDateString()
        );
        if (target) target.events.push(ev);
      }
    });

    this.calendarDays.forEach(d => {
      d.events.sort((a, b) => (a.startTime > b.startTime ? 1 : a.startTime < b.startTime ? -1 : 0));
    });

    if (this.selectedView === 'week') {
      this.populateWeekEvents();
    }
  }

  private populateWeekEvents(): void {
    this.weekViewDays.forEach(day => {
      const matchingDay = this.calendarDays.find(d => d.date.toDateString() === day.date.toDateString());
      day.events = matchingDay?.events || [];
    });
  }

  private getEventDate(event: CalendarEvent): Date | null {
    return isNaN(event.scheduledDate.getTime()) ? null : event.scheduledDate;
  }

  private getDayKey(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /** Intl returns lowercase month/weekday names in French and Spanish; headings expect a capital. */
  private capitalizeFirst(value: string): string {
    return value ? value.charAt(0).toLocaleUpperCase() + value.slice(1) : value;
  }
}